import React, { useCallback, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { MdDarkMode, MdLightMode, MdLogout, MdSwapHoriz } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import { useBrandPalette } from '../../contexts/BrandPaletteContext';
import { BrandLogo, OutlineButton, PrimaryButton } from '../../components/ui';
import { spectrumColors } from '../../theme/colors';
import { brandBarMotion } from '../../theme/brandMotion';

/**
 * Full-screen Hub shell — no classic sidebar, no classic header chrome.
 * Compact controls only below `md`; desktop keeps the original header layout.
 */
export default function HubLayout() {
  const navigate = useNavigate();
  const { logout, staff } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const { paletteId } = useBrandPalette();
  const displayName = staff?.fullName || staff?.name || staff?.email || 'Staff';
  const [homeTick, setHomeTick] = useState(0);
  const spectrum = paletteId === 'spectrum';

  const goHubHome = useCallback(() => {
    setHomeTick((n) => n + 1);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
  };

  return (
    <Flex direction="column" h="100vh" overflow="hidden" bg="bg.canvas">
      <Flex
        as="header"
        align="center"
        justify="space-between"
        px={{ base: 3, md: 6 }}
        py={{ base: 2, md: 3 }}
        borderBottom="1px solid"
        borderColor="border.subtle"
        bg="bg.surface"
        flexShrink={0}
        gap={{ base: 2, md: 3 }}
      >
        <Flex
          as="button"
          type="button"
          align="center"
          gap={3}
          minW={0}
          onClick={goHubHome}
          cursor="pointer"
          bg="transparent"
          border="none"
          p={0}
          _hover={{ opacity: 0.85 }}
          aria-label="Go to Hub home"
        >
          <BrandLogo
            variant="full"
            h={{ base: '32px', md: '40px' }}
            maxW={{ base: '132px', md: '168px' }}
            plate="auto"
            shimmer
          />
          <Text
            fontSize="13px"
            color="text.secondary"
            display={{ base: 'none', md: 'block' }}
          >
            Hub view
          </Text>
        </Flex>

        <Flex align="center" gap={2} flexShrink={0}>
          <Text
            fontSize="13px"
            color="text.primary"
            noOfLines={1}
            display={{ base: 'none', md: 'block' }}
            maxW="160px"
          >
            {displayName}
          </Text>
          <IconButton
            aria-label={
              colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'
            }
            icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
            onClick={toggleColorMode}
            variant="ghost"
            color="text.secondary"
            size="sm"
            _hover={{ bg: 'bg.input' }}
          />

          {/* Mobile: short Classic + icon logout */}
          <PrimaryButton
            size="xs"
            leftIcon={<Icon as={MdSwapHoriz} />}
            onClick={() => navigate('/admin/dashboard')}
            display={{ base: 'inline-flex', md: 'none' }}
          >
            Classic
          </PrimaryButton>
          <IconButton
            aria-label="Logout"
            icon={<MdLogout />}
            onClick={handleLogout}
            variant="outline"
            borderColor="border.subtle"
            color="text.primary"
            size="sm"
            display={{ base: 'inline-flex', md: 'none' }}
            _hover={{ bg: 'bg.input' }}
          />

          {/* Desktop: original labeled buttons */}
          <PrimaryButton
            size="sm"
            leftIcon={<Icon as={MdSwapHoriz} />}
            onClick={() => navigate('/admin/dashboard')}
            display={{ base: 'none', md: 'inline-flex' }}
          >
            Switch to Classic
          </PrimaryButton>
          <OutlineButton
            size="sm"
            onClick={handleLogout}
            display={{ base: 'none', md: 'inline-flex' }}
          >
            Logout
          </OutlineButton>
        </Flex>
      </Flex>

      {spectrum ? (
        <Box
          h="3px"
          w="100%"
          flexShrink={0}
          bgImage={spectrumColors.brandBarGradient}
          sx={brandBarMotion('8s')}
          aria-hidden
        />
      ) : null}

      <Box as="main" flex="1" minH={0} overflowY="auto" w="100%" bg="bg.canvas">
        <Outlet context={{ hubHomeTick: homeTick }} />
      </Box>
    </Flex>
  );
}
